import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { positionService } from '../services/positionService';
import type { Position } from '../services/positionService';

const VALID_ROLES = [
  { value: 'Admin', label: 'Admin' },
  { value: 'Approver', label: 'BGH' },
  { value: 'Librarian', label: 'Thủ thư' },
  { value: 'Teacher', label: 'Giáo viên' },
  { value: 'Student', label: 'Học sinh' }
];

export const PositionsManagementTab: React.FC = () => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Use a Map to store edit state per position to avoid shared state bug
  const [editMappedRolesMap, setEditMappedRolesMap] = useState<Record<string, string[]>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    loadPositions();
  }, []);

  const loadPositions = async () => {
    setLoading(true);
    try {
      const data = await positionService.getPositions();
      setPositions(data);
    } catch (error) {
      console.error('Error loading positions:', error);
      setSnackbar({ open: true, message: 'Lỗi khi tải danh sách chức vụ', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (position: Position) => {
    setEditingId(position.id);
    // Store the edit roles for this specific position
    setEditMappedRolesMap(prev => ({
      ...prev,
      [position.id]: [...(position.mappedRoles || [])]
    }));
  };

  const handleSave = async (positionId: string) => {
    try {
      const position = positions.find(p => p.id === positionId);
      const editMappedRoles = editMappedRolesMap[positionId] || [];
      await positionService.updatePosition(positionId, {
        // Send full payload to be compatible with backend validation and keep name stable
        name: position?.name,
        description: position?.description,
        mappedRoles: editMappedRoles
      });
      setSnackbar({ open: true, message: 'Cập nhật chức vụ thành công', severity: 'success' });
      setEditingId(null);
      // Clean up the edit state for this position
      setEditMappedRolesMap(prev => {
        const { [positionId]: _, ...rest } = prev;
        return rest;
      });
      await loadPositions();
    } catch (error) {
      console.error('Error updating position:', error);
      setSnackbar({ open: true, message: 'Lỗi khi cập nhật chức vụ', severity: 'error' });
    }
  };

  const handleCancel = () => {
    // Clean up edit state when canceling
    if (editingId) {
      setEditMappedRolesMap(prev => {
        const { [editingId]: _, ...rest } = prev;
        return rest;
      });
    }
    setEditingId(null);
  };

  const toggleRole = (positionId: string, role: string) => {
    setEditMappedRolesMap(prev => {
      const currentRoles = prev[positionId] || [];
      const newRoles = currentRoles.includes(role)
        ? currentRoles.filter(r => r !== role)
        : [...currentRoles, role];
      return {
        ...prev,
        [positionId]: newRoles
      };
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>

      <TableContainer component={Paper}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '12%' }}>ID Oracle</TableCell>
              <TableCell sx={{ width: '18%' }}>Tên chức vụ</TableCell>
              <TableCell sx={{ width: '30%' }}>Mô tả</TableCell>
              <TableCell sx={{ width: '32%' }}>Vai trò được ánh xạ</TableCell>
              <TableCell sx={{ width: '8%' }}>Hành động</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Chưa có chức vụ nào. Chức vụ sẽ được đồng bộ từ Oracle khi tạo thư viện hoặc đồng bộ lại.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              positions.map((position) => (
                <TableRow key={position.id}>
                  <TableCell>{position.oracleId || '-'}</TableCell>
                  <TableCell>{position.name}</TableCell>
                  <TableCell>{position.description || '-'}</TableCell>
                  <TableCell>
                    {editingId === position.id ? (
                      <Box>
                        {VALID_ROLES.map((role) => (
                          <FormControlLabel
                            key={role.value}
                            control={
                              <Checkbox
                                checked={(editMappedRolesMap[position.id] || []).includes(role.value)}
                                onChange={() => toggleRole(position.id, role.value)}
                                size="small"
                              />
                            }
                            label={role.label}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {position.mappedRoles && position.mappedRoles.length > 0 ? (
                          position.mappedRoles.map((role) => {
                            const roleInfo = VALID_ROLES.find(r => r.value === role);
                            return (
                              <Chip
                                key={role}
                                label={roleInfo?.label || role}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            );
                          })
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Chưa ánh xạ
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === position.id ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSave(position.id)}
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={handleCancel}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(position)}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

